import { CURRENT_DB_VERSION, applyMigrations, DB_NAME } from './dbMigrations.js';

export { DB_NAME };
export const DB_VERSION = CURRENT_DB_VERSION;

const METADATA = 'metadata';

let db = null;
let initPromise = null;

export function initDB() {
    if (db) return Promise.resolve(db);
    if (initPromise) return initPromise;
    
    initPromise = new Promise((resolve, reject) => {
        const request = indexedDB.open(DB_NAME, DB_VERSION);
        
        request.onupgradeneeded = (event) => {
            const dbInstance = event.target.result;
            console.log(`Upgrading database from version ${event.oldVersion} to ${DB_VERSION}`);
            applyMigrations(dbInstance, event.target.transaction, event.oldVersion || 0, DB_VERSION);
        };
        
        request.onsuccess = (event) => {
            db = event.target.result;
            initPromise = null;
            resolve(db);
        };
        
        request.onerror = (event) => {
            initPromise = null;
            reject(event.target.error);
        };
    });
    
    return initPromise;
}

const getStore = async (storeName, mode = 'readonly') => {
    if (!db) db = await initDB();
    return db.transaction(storeName, mode).objectStore(storeName);
};

const createPromise = (storeName, mode, operation) => 
    new Promise(async (resolve, reject) => {
        try {
            const store = await getStore(storeName, mode);
            const request = operation(store);
            request.onsuccess = () => resolve(request.result);
            request.onerror = (event) => reject(event.target.error);
        } catch (error) {
            reject(error);
        }
    });

export const saveMetadata = (key, value) => 
    createPromise(METADATA, 'readwrite', store => store.put({ key, value }))
        .then(() => key);

export const getMetadata = (key) => {
    if (!key) {
        return createPromise(METADATA, 'readonly', store => store.getAll())
            .then(results => Object.fromEntries(results.map(item => [item.key, item.value])));
    }
    return createPromise(METADATA, 'readonly', store => store.get(key))
        .then(result => result?.value ?? null);
};

export const clearDatabase = async () => {
    const database = await initDB();
    const storeNames = Array.from(database.objectStoreNames || []);

    if (!storeNames.length) return;

    await Promise.all(storeNames.map(storeName =>
        createPromise(storeName, 'readwrite', store => store.clear())
    ));
};
